import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef
} from 'react';
import {
  Modal,
  ModalContent,
  ModalFooter,
  Button,
  Icon,
  Tooltip
} from '@deque/cauldron-react';
import { useTranslation } from 'react-i18next';
import Screenshot from './Screenshot';
import styles from './ScreenshotDialog.css';
import type { BoundingBox, ScreenshotMetadata } from '../../api-client';

interface Props {
  issueHelp: string;
  boundingBox: BoundingBox | null;
  screenshot: Blob;
  metadata: ScreenshotMetadata | null;
  issueOrder: number;
  showDialog: boolean;
  onClose: () => void;
}

const ScreenshotDialog: React.FC<Props> = ({
  issueHelp,
  boundingBox,
  screenshot,
  metadata,
  issueOrder,
  showDialog,
  onClose
}) => {
  const { t } = useTranslation();
  const screenshotRef = useRef<HTMLImageElement | null>(null);
  const highlightButtonRef = useRef<HTMLButtonElement>(null);
  const [scaleDownRatio, setScaleDownRatio] = useState<number>(1);
  const [showHighlight, setShowHighlight] = useState(!!boundingBox);
  const screenshotIsOffscreen =
    boundingBox?.width === 0 && boundingBox?.height === 0;

  const getCurrentRatio = () => {
    if (!screenshotRef.current) {
      return;
    }
    // naturalWidth comes back 0 during initial render
    const originalWidth =
      screenshotRef.current.naturalWidth || metadata?.width || 1;
    const ratio = screenshotRef.current.width / originalWidth || 1;
    setScaleDownRatio(ratio);
  };

  // get ratio for initial render
  const setScreenshotRef = useCallback(ref => {
    if (!ref) {
      return;
    }

    screenshotRef.current = ref;
    getCurrentRatio();
  }, []);

  // get ratio on screenshot resize
  useEffect(() => {
    if (!screenshotRef.current) {
      return;
    }
    const observer = new ResizeObserver(() => {
      getCurrentRatio();
    });
    observer.observe(screenshotRef.current);
    return () => observer.disconnect();
  }, [screenshotRef.current]);

  const highlightStyles = useMemo(() => {
    if (!boundingBox || !scaleDownRatio) {
      return;
    }

    return {
      width: `${boundingBox.width * scaleDownRatio}px`,
      height: `${boundingBox.height * scaleDownRatio}px`,
      top: `${boundingBox.y * scaleDownRatio}px`,
      left: `${boundingBox.x * scaleDownRatio}px`
    } as React.CSSProperties;
  }, [boundingBox, scaleDownRatio]);

  const highlightRef = useCallback((ref: HTMLDivElement) => {
    if (!ref || screenshotIsOffscreen) {
      return;
    }

    ref.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
      inline: 'nearest'
    });
  }, []);

  // not to expand the image
  const screenshotStyles = useMemo(() => {
    if (!metadata) {
      return;
    }

    return {
      maxWidth: `${metadata.width}px`,
      maxHeight: `${metadata.height}px`
    } as React.CSSProperties;
  }, [metadata]);

  return (
    <Modal
      show={showDialog}
      heading={t(`Screenshot: {{ruleHelpText}} {{issueOrder}}`, {
        ruleHelpText: issueHelp,
        issueOrder
      })}
      className={styles.screenshotDialog}
      onClose={onClose}
    >
      <ModalContent>
        <div className={styles.screenshotWrapper}>
          <Screenshot
            screenshot={screenshot}
            screenshotRef={setScreenshotRef}
            screenshotStyles={screenshotStyles}
          />
          {showHighlight && highlightStyles && !screenshotIsOffscreen && (
            <div
              data-testid="screenshot-highlight"
              className={styles.highlight}
              style={highlightStyles}
              ref={highlightRef}
            ></div>
          )}
        </div>
      </ModalContent>
      <ModalFooter>
        {boundingBox && (
          <>
            {screenshotIsOffscreen && (
              <Tooltip
                variant="info"
                target={highlightButtonRef}
                placement="top"
              >
                {t(
                  'We are unable to highlight the element because it is scrolled out of view in the screenshot. This can happen when elements are offscreen or within scrollable panes.'
                )}
              </Tooltip>
            )}
            <Button
              ref={highlightButtonRef}
              variant="secondary"
              role="switch"
              aria-checked={!screenshotIsOffscreen && showHighlight}
              className={styles.highlightButton}
              onClick={() =>
                !screenshotIsOffscreen && setShowHighlight(!showHighlight)
              }
              aria-disabled={screenshotIsOffscreen}
            >
              <Icon type="highlight" />
              {t('Highlight Issue')}
            </Button>
          </>
        )}
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ScreenshotDialog;
